import { useEffect, useRef } from 'react';
import { useMediaCapture } from '@features/media-capture';
import { PlaceholderIcon } from '@shared/ui';

export function PreviewSurface() {
  const { stream, state, settings } = useMediaCapture();
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.srcObject = stream;
    }
  }, [stream]);

  const isLive = state === 'active';

  return (
    <div className="relative aspect-video w-full overflow-hidden rounded-xl border border-surface-border bg-black">
      <video
        ref={videoRef}
        autoPlay
        muted
        playsInline
        className={`h-full w-full object-cover ${isLive ? 'opacity-100' : 'opacity-0'}`}
      />

      {!isLive && (
        <div className="absolute inset-0 flex items-center justify-center bg-surface-1/60">
          <p className="text-sm text-text-muted">
            {state === 'requesting-permissions'
              ? 'Requesting camera & microphone access…'
              : 'Camera preview will appear here'}
          </p>
        </div>
      )}

      {/* Resolution / FPS badge, top-left */}
      <div className="absolute left-3 top-3 rounded-md bg-black/60 px-2.5 py-1 text-xs font-medium text-text-primary backdrop-blur-sm">
        {settings.resolution.height}p &bull; {settings.frameRate} FPS
      </div>

      {/* PREVIEW indicator, top-right */}
      <div className="absolute right-3 top-3 flex items-center gap-1.5 rounded-md bg-black/60 px-2.5 py-1 text-xs font-medium text-text-primary backdrop-blur-sm">
        <span className="h-1.5 w-1.5 rounded-full bg-status-live" />
        PREVIEW
      </div>

      {/* Avatar overlay placeholder — reserved for future Unity renderer */}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <div className="flex items-center gap-3 rounded-lg border border-dashed border-text-muted/40 bg-surface-1/70 px-5 py-3 backdrop-blur-sm">
          <PlaceholderIcon className="h-6 w-6 text-text-muted" />
          <div className="text-left">
            <div className="text-sm font-medium text-text-secondary">Transparent Avatar Layer</div>
            <div className="text-xs text-text-muted">(Unity Renderer Placeholder)</div>
          </div>
        </div>
      </div>
    </div>
  );
}
